import React, { useState } from "react";
import Icon from "./UI/icon";
import {
  faEye,
  faPencil,
  faTrash,
  faClock,
  faPhone,
  faStar,
  faUtensils,
  faChair,
} from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarOutline } from "@fortawesome/free-regular-svg-icons";
import { category } from "../constants/category";
import UserPopup from "./UI/userDetailsPopup";
import TablePickerPopup from "./UI/tablePickerPopup";
import { Link } from "react-router-dom";
import { getDatabase, ref, get, update } from "firebase/database";
import { toastrError, toastrSuccess } from "./toastr";

const statusStyles = {
  waiting:  "bg-white border border-yellow-200",
  seated:   "bg-white border border-blue-200",
  done:     "bg-white border border-green-200",
  canceled: "bg-white border border-red-200",
};

const statusBadge = {
  waiting:  "bg-yellow-50 text-yellow-700",
  seated:   "bg-blue-50 text-blue-700",
  done:     "bg-green-50 text-green-700",
  canceled: "bg-red-50 text-red-700",
};

const statusDot = {
  waiting:  "bg-yellow-400",
  seated:   "bg-blue-400",
  done:     "bg-green-400",
  canceled: "bg-red-400",
};

const statusLabel = {
  waiting:  "Waiting",
  seated:   "Seated",
  done:     "Done",
  canceled: "Canceled",
};

function UsersTable(props) {
  const { setLoader, customers, onStartUndo } = props;
  const [view, setView] = useState(false);
  const [userData, setUserData] = useState();
  const [tablePickerCustomer, setTablePickerCustomer] = useState(null);
  const ownerId = localStorage.getItem("ownerId");
  const database = getDatabase();

  const handleStatusUpdate = (customer, action, extra = {}) => {
    setLoader(true);
    const userSnapshot = ref(database, "customers");
    get(userSnapshot).then((snapshot) => {
      if (snapshot.exists()) {
        const allData = snapshot.val();
        const userKey = Object.keys(allData).find(
          (key) => allData[key].timeStamp === customer.timeStamp
        );
        if (!userKey) { setLoader(false); return; }

        const customerRef = ref(database, `customers/${userKey}`);
        let newStatus;
        if (action === "checkin")  newStatus = "seated";
        else if (action === "done") newStatus = "done";
        else if (action === "delete") newStatus = "canceled";

        update(customerRef, { ...customer, status: newStatus, ...extra })
          .then(async () => {
            // Release table when customer is done or canceled
            if ((action === "done" || action === "delete") && customer.tableId && ownerId) {
              await update(ref(database, `tables/${ownerId}/${customer.tableId}`), {
                status: "free",
                currentCustomerId: null,
              }).catch(() => {});
            }
            setLoader(false);
            toastrSuccess("Customer updated");
          })
          .catch((error) => {
            setLoader(false);
            toastrError("Error updating customer: ", error);
          });
      }
    });
  };

  const handleTogglePriority = (customer) => {
    setLoader(true);
    const userSnapshot = ref(database, "customers");
    get(userSnapshot).then((snapshot) => {
      if (snapshot.exists()) {
        const allData = snapshot.val();
        const userKey = Object.keys(allData).find(
          (key) => allData[key].timeStamp === customer.timeStamp
        );
        if (!userKey) { setLoader(false); return; }
        const customerRef = ref(database, `customers/${userKey}`);
        update(customerRef, { isPriority: !customer.isPriority })
          .then(() => setLoader(false))
          .catch(() => setLoader(false));
      }
    });
  };

  const handleCheckIn = (customer) => {
    setTablePickerCustomer(customer);
  };

  const confirmCheckIn = async (customer, table) => {
    setTablePickerCustomer(null);
    const extra = table
      ? { tableNumber: table.number, tableId: table.id }
      : {};
    // Mark table occupied
    if (table && ownerId) {
      await update(ref(database, `tables/${ownerId}/${table.id}`), {
        status: "occupied",
        currentCustomerId: customer.id,
      }).catch(() => {});
    }
    handleStatusUpdate(customer, "checkin", extra);
    if (onStartUndo) onStartUndo(customer, "seated");
  };

  const handleMarkDone = (customer) => {
    handleStatusUpdate(customer, "done");
    if (onStartUndo) onStartUndo(customer, "done");
  };

  const calculateWaitingTime = (timeStamp) => {
    const now = new Date();
    const estimateTime = new Date(timeStamp);
    const difference = now - estimateTime;
    const minutes = Math.floor(difference / 60000);
    return `${minutes}m`;
  };

  const getCategoryIcon = (categoryValue) => {
    const cat = category.find((c) => c.value === categoryValue);
    return cat ? cat.icon : null;
  };

  const onClose = () => setView(false);

  return (
    <div className="mx-4 flex flex-col gap-2.5">
      {customers.map((custom) => (
        <div
          key={custom.timeStamp}
          className={`rounded-2xl shadow-card p-4 ${
            custom.isPriority && custom.status === "waiting"
              ? "border-l-4 border-l-yellow-400 " + (statusStyles[custom.status] || "bg-white border border-gray-200")
              : statusStyles[custom.status] || "bg-white border border-gray-200"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-primary-bg flex items-center justify-center">
                {getCategoryIcon(custom.category) ? (
                  <Icon icon={getCategoryIcon(custom.category)} className="text-primary text-sm" />
                ) : (
                  <span className="text-sm font-bold text-primary">
                    {custom.userName ? custom.userName.charAt(0).toUpperCase() : "?"}
                  </span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900">{custom.userName}</span>
                  {custom.tokenNumber && (
                    <span className="text-xs bg-primary-bg text-primary px-1.5 py-0.5 rounded font-bold">
                      #{custom.tokenNumber}
                    </span>
                  )}
                  {custom.isPriority && (
                    <span className="text-xs bg-yellow-50 text-yellow-600 px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
                      <Icon icon={faStar} className="text-xs" /> VIP
                    </span>
                  )}
                  {custom.tableNumber && (
                    <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
                      <Icon icon={faChair} className="text-xs" /> T{custom.tableNumber}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${statusDot[custom.status] || "bg-gray-400"}`} />
                  <span className="text-xs text-gray-500 capitalize">
                    {statusLabel[custom.status] || custom.status}
                  </span>
                </div>
              </div>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-lg font-medium ${statusBadge[custom.status] || "bg-gray-100 text-gray-800"}`}>
              {statusLabel[custom.status] || custom.status}
            </span>
          </div>

          {/* Info row */}
          <div className="flex items-center gap-4 text-xs text-gray-500 mb-3 bg-gray-50 rounded-xl px-3 py-2">
            <div className="flex items-center gap-1">
              <Icon icon={faClock} className="text-gray-400 text-xs" />
              <span>
                {(custom.status === "waiting" || custom.status === "seated")
                  ? calculateWaitingTime(custom.timeStamp)
                  : "-"}
              </span>
            </div>
            <div><span className="text-gray-400">Est:</span> {custom.estimate}m</div>
            <div><span className="text-gray-400">Count:</span> {custom.count}</div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {custom.status === "waiting" && (
              <button
                onClick={() => handleCheckIn(custom)}
                className="flex items-center gap-1.5 text-xs font-semibold bg-blue-50 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-100"
              >
                <Icon icon={faChair} className="text-xs" />
                Check-in
              </button>
            )}
            {custom.status === "seated" && (
              <button
                onClick={() => handleMarkDone(custom)}
                className="flex items-center gap-1.5 text-xs font-semibold bg-green-50 text-green-700 px-3 py-2 rounded-lg hover:bg-green-100"
              >
                <Icon icon={faUtensils} className="text-xs" />
                Mark Done
              </button>
            )}
            {custom.mobileNumber && (
              <a
                href={`tel:${custom.mobileNumber}`}
                className="flex items-center gap-1.5 text-xs font-semibold bg-purple-50 text-primary px-3 py-2 rounded-lg hover:bg-purple-100"
              >
                <Icon icon={faPhone} className="text-xs" />
                Call
              </a>
            )}
            <button
              onClick={() => { setView(true); setUserData(custom); }}
              className="flex items-center gap-1.5 text-xs font-semibold bg-primary-bg text-primary px-3 py-2 rounded-lg hover:bg-purple-100"
            >
              <Icon icon={faEye} className="text-xs" />
              View
            </button>
            {(custom.status === "waiting" || custom.status === "seated") && (
              <Link to={`/addUser/${custom.id}`}>
                <span className="flex items-center gap-1.5 text-xs font-semibold bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200">
                  <Icon icon={faPencil} className="text-xs" />
                  Edit
                </span>
              </Link>
            )}
            {custom.status === "waiting" && (
              <>
                <button
                  onClick={() => handleTogglePriority(custom)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg ${
                    custom.isPriority
                      ? "bg-yellow-50 text-yellow-600 hover:bg-yellow-100"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                  title={custom.isPriority ? "Remove priority" : "Mark as priority"}
                >
                  <Icon icon={custom.isPriority ? faStar : faStarOutline} className="text-xs" />
                </button>
                <button
                  onClick={() => handleStatusUpdate(custom, "delete")}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 ml-auto"
                >
                  <Icon icon={faTrash} className="text-xs" />
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      ))}
      {view && <UserPopup details={userData} view={view} onClose={onClose} />}
      {tablePickerCustomer && (
        <TablePickerPopup
          ownerId={ownerId}
          onSelect={(table) => confirmCheckIn(tablePickerCustomer, table)}
          onNoTable={() => confirmCheckIn(tablePickerCustomer, null)}
          onCancel={() => setTablePickerCustomer(null)}
        />
      )}
    </div>
  );
}

export default UsersTable;
